import { useState } from 'react';
import { toast } from 'sonner';
import { dashboardApi } from '@/services/dashboardApi';
import { inputCls, btnGold, Badge, todayLocal } from './ui';

// Registro único de una operación (sustituye a la hoja ENTRADA del CDM)
const KINDS = {
  membership: 'Membresía (alta o pago)',
  included: 'Alta sin cobro (cedida / pago en trabajo)',
  fhct: 'Aportación FHCT',
  reservation: 'Reserva de membresía',
  investment: 'Aportación de capital',
  payment: 'Otro cobro',
  expense: 'Gasto',
};
// Valores por defecto: los buenos llegan del backend (cdm/settings.py → /status.lists)
const DEF = {
  payment_method: ['Transferencia', 'Tarjeta', 'Cripto', 'Efectivo', 'Compensación'],
  expense_category: ['Publicidad', 'Nóminas', 'Comisiones', 'Herramientas y licencias', 'Servicios profesionales', 'Desarrollo tecnológico', 'Diseño y audiovisual', 'Viajes', 'Eventos y bootcamp', 'Alquiler Amaroo a CBC', 'Impuestos', 'Pérdida por el balsero', 'Otros', 'Aportación a operación', 'Devolución a socios'],
  sku: ['Access', 'PRO', 'Premium'],
  channel: ['Redes sociales', 'Anuncio pagado', 'Referido de miembro', 'Broker/Afiliado', 'Directo'],
  included_reason: ['No', 'Acuerdo dirección', 'Deferencia CBC', 'Bootcamp Balsero', 'Por revisar'],
  instrument: ['Equity', 'Préstamo convertible', 'Aportación a cuenta', 'Por definir'],
};
// Orígenes de FHCT que se pueden registrar a mano (sin_cobro y por_revisar son estados de revisión)
const FUNDING_KEYS = ['dinero_real', 'w2usdt', 'balance_plataforma', 'incluido_membresia'];
const MODES = ['Anual', 'Vitalicia'];

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">{label}</span>
    <div className="mt-1">{children}</div>
    {hint && <span className="text-xs text-gray-400">{hint}</span>}
  </label>
);

export default function CdmRegistrar({ cdm }) {
  const L = { ...DEF, ...(cdm?.lists || {}) };
  const FUNDING = Object.fromEntries(FUNDING_KEYS.map((k) => [k, (cdm?.lists?.fhct_funding || {})[k] || k]));
  const today = todayLocal();
  const blank = { kind: 'membership', date: today, member_id: '', isNew: false, amount: '', currency: 'EUR', method: 'Transferencia',
    sku: 'PRO', funding: 'dinero_real', units: '', concept: '', category: 'Otros', supplier: '', recurring: false,
    instrument: 'Por definir', payment_type: 'Formación', notes: '',
    nm: { name: '', email: '', phone: '', country: '', sponsor_id: '', agreed_price: '', due_date: '', membership_type: 'Anual', channel: '' },
    inc: { id: '', reason: 'Acuerdo dirección' } };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const setNm = (k) => (e) => setF({ ...f, nm: { ...f.nm, [k]: k === 'sponsor_id' ? e.target.value.toUpperCase() : e.target.value } });
  const canNew = f.kind === 'membership' || f.kind === 'reservation';
  // Access solo es anual: al elegir Access la modalidad vuelve a Anual
  const setSku = (e) => { const sku = e.target.value; setF({ ...f, sku, nm: { ...f.nm, membership_type: sku === 'Access' ? 'Anual' : f.nm.membership_type } }); };
  const modeSelect = (
    <Field label="Modalidad" hint={f.sku === 'Access' ? 'Access solo existe como membresía anual' : undefined}>
      <select value={f.nm.membership_type} onChange={setNm('membership_type')} className={`${inputCls} w-full`}>
        {MODES.map((m) => <option key={m} value={m} disabled={f.sku === 'Access' && m === 'Vitalicia'} className="bg-[#0d1829]">{m}</option>)}
      </select>
    </Field>
  );
  const channelSelect = (
    <Field label="Canal de entrada">
      <select value={f.nm.channel} onChange={setNm('channel')} className={`${inputCls} w-full`}>
        <option value="" className="bg-[#0d1829]">Sin indicar</option>
        {L.channel.map((c) => <option key={c} value={c} className="bg-[#0d1829]">{c}</option>)}
      </select>
    </Field>
  );

  const submit = async (e) => {
    e.preventDefault();
    if (f.kind === 'included') {
      setBusy(true);
      try {
        const r = await dashboardApi.cdmIncludedMember({ id: f.inc.id.trim().toUpperCase() || null, join_date: f.date, name: f.nm.name,
          email: f.nm.email || null, sku: f.sku, sponsor_id: f.nm.sponsor_id, included_reason: f.inc.reason, notes: f.notes || null,
          membership_type: f.sku === 'Access' ? 'Anual' : f.nm.membership_type, channel: f.nm.channel || null });
        setLast(r.created);
        toast.success('Alta registrada sin cobro');
        setF({ ...blank, kind: f.kind, date: f.date, nm: { ...blank.nm, membership_type: 'Vitalicia' } });
      } catch (err) { toast.error(err.message); } finally { setBusy(false); }
      return;
    }
    const body = { kind: f.kind, date: f.date, amount: Number(f.amount), currency: f.currency, notes: f.notes || null };
    if (f.kind === 'expense') Object.assign(body, { category: f.category, concept: f.concept || null, supplier: f.supplier || null, recurring: f.recurring });
    else {
      body.method = f.method;
      if (f.isNew && canNew) {
        body.new_member = { ...f.nm, sku: f.sku, agreed_price: f.nm.agreed_price ? Number(f.nm.agreed_price) : undefined,
          membership_type: f.sku === 'Access' ? 'Anual' : f.nm.membership_type, channel: f.nm.channel || null };
      }
      else {
        body.member_id = f.member_id.trim().toUpperCase();
        // La reserva de un socio existente también necesita el precio acordado (el backend lo lee de new_member)
        if (f.kind === 'reservation') body.new_member = { sku: f.sku, agreed_price: Number(f.nm.agreed_price), due_date: f.nm.due_date || null };
      }
      if (f.kind === 'membership') body.sku = f.sku;
      if (f.kind === 'fhct') { body.funding = f.funding; if (f.units) body.units = Number(f.units); }
      if (f.kind === 'investment') body.instrument = f.instrument;
      if (f.kind === 'payment') body.payment_type = f.payment_type;
      if (f.concept) body.concept = f.concept;
    }
    setBusy(true);
    try {
      const r = await dashboardApi.cdmOperation(body);
      setLast(r.created);
      toast.success('Operación registrada');
      setF({ ...blank, kind: f.kind, date: f.date });
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-5 max-w-3xl" data-testid="cdm-registrar">
      <div className="flex flex-wrap gap-1">
        {Object.entries(KINDS).map(([k, v]) => (
          <button type="button" key={k} onClick={() => setF({ ...f, kind: k, isNew: false, nm: { ...f.nm, membership_type: k === 'included' && f.sku !== 'Access' ? 'Vitalicia' : 'Anual' } })}
            className={`px-3 py-1.5 rounded-full text-xs border ${f.kind === k ? 'border-[#c9a84c]/40 text-[#c9a84c] bg-[#c9a84c]/10' : 'border-white/10 text-gray-400'}`}>{v}</button>
        ))}
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <Field label={f.kind === 'included' ? 'Fecha de alta' : 'Fecha'}><input type="date" required value={f.date} onChange={set('date')} className={`${inputCls} w-full`} /></Field>
        {f.kind !== 'included' && (<>
        <Field label="Importe"><input type="number" required min="0.01" step="0.01" value={f.amount} onChange={set('amount')} className={`${inputCls} w-full`} /></Field>
        <Field label="Divisa">
          <select value={f.currency} onChange={set('currency')} className={`${inputCls} w-full`}><option className="bg-[#0d1829]">EUR</option><option className="bg-[#0d1829]">USD</option></select>
        </Field>
        </>)}
      </div>

      {f.kind === 'included' ? (
        <div className="grid sm:grid-cols-2 gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4" data-testid="cdm-included">
          <p className="sm:col-span-2 text-xs text-gray-400">Socio que no paga membresía (cedida o pagada con trabajo). No genera cobro ni comisiones. Usa el mismo ID que en la hoja si ya lo tiene.</p>
          <Field label="ID (opcional)" hint="Vacío = el siguiente libre"><input value={f.inc.id} onChange={(e) => setF({ ...f, inc: { ...f.inc, id: e.target.value.toUpperCase() } })} className={`${inputCls} w-full`} placeholder="B70" /></Field>
          <Field label="Membresía"><select value={f.sku} onChange={setSku} className={`${inputCls} w-full`}>{L.sku.map((s) => <option key={s} className="bg-[#0d1829]">{s}</option>)}</select></Field>
          {modeSelect}
          {channelSelect}
          <Field label="Nombre completo"><input required value={f.nm.name} onChange={setNm('name')} className={`${inputCls} w-full`} /></Field>
          <Field label="Broker / origen (ID)"><input required value={f.nm.sponsor_id} onChange={setNm('sponsor_id')} className={`${inputCls} w-full`} placeholder="EI5" /></Field>
          <Field label="Email"><input type="email" value={f.nm.email} onChange={setNm('email')} className={`${inputCls} w-full`} /></Field>
          <Field label="Motivo"><select value={f.inc.reason} onChange={(e) => setF({ ...f, inc: { ...f.inc, reason: e.target.value } })} className={`${inputCls} w-full`}>{L.included_reason.filter((s) => s !== 'No').map((s) => <option key={s} className="bg-[#0d1829]">{s}</option>)}</select></Field>
        </div>
      ) : f.kind === 'expense' ? (
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Partida"><select value={f.category} onChange={set('category')} className={`${inputCls} w-full`}>{L.expense_category.map((c) => <option key={c} className="bg-[#0d1829]">{c}</option>)}</select></Field>
          <Field label="Proveedor"><input value={f.supplier} onChange={set('supplier')} className={`${inputCls} w-full`} /></Field>
          <Field label="Concepto"><input value={f.concept} onChange={set('concept')} className={`${inputCls} w-full`} /></Field>
          <label className="flex items-center gap-2 text-sm text-gray-300 mt-6"><input type="checkbox" checked={f.recurring} onChange={set('recurring')} /> Gasto recurrente</label>
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Método de cobro"><select value={f.method} onChange={set('method')} className={`${inputCls} w-full`}>{L.payment_method.map((m) => <option key={m} className="bg-[#0d1829]">{m}</option>)}</select></Field>
            {(f.kind === 'membership' || f.kind === 'reservation') && (
              <Field label="Membresía"><select value={f.sku} onChange={setSku} className={`${inputCls} w-full`}>{L.sku.map((s) => <option key={s} className="bg-[#0d1829]">{s}</option>)}</select></Field>
            )}
            {f.kind === 'fhct' && (<>
              <Field label="Cómo se paga"><select value={f.funding} onChange={set('funding')} className={`${inputCls} w-full`}>{Object.entries(FUNDING).map(([k, v]) => <option key={k} value={k} className="bg-[#0d1829]">{v}</option>)}</select></Field>
              <Field label="FHCT" hint="Vacío = 1 FHCT por dólar (los euros se pasan a dólares con el cambio de gestión)"><input type="number" min="0" value={f.units} onChange={set('units')} className={`${inputCls} w-full`} /></Field>
            </>)}
            {f.kind === 'investment' && (
              <Field label="Instrumento"><select value={f.instrument} onChange={set('instrument')} className={`${inputCls} w-full`}>{L.instrument.map((s) => <option key={s} className="bg-[#0d1829]">{s}</option>)}</select></Field>
            )}
            {f.kind === 'payment' && (
              <Field label="Tipo de cobro"><select value={f.payment_type} onChange={set('payment_type')} className={`${inputCls} w-full`}>{['Upgrade', 'Formación', 'Otro'].map((s) => <option key={s} value={s} className="bg-[#0d1829]">{s === 'Upgrade' ? 'Upgrade de membresía (comisiona)' : s}</option>)}</select></Field>
            )}
          </div>
          {canNew && (
            <label className="flex items-center gap-2 text-sm text-gray-300"><input type="checkbox" checked={f.isNew} onChange={set('isNew')} /> Es un socio nuevo</label>
          )}
          {f.isNew && canNew ? (
            <div className="grid sm:grid-cols-2 gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
              <Field label="Nombre completo"><input required value={f.nm.name} onChange={setNm('name')} className={`${inputCls} w-full`} /></Field>
              <Field label="Broker que lo trae (ID)" hint="Solo esta vez: después se hereda de su ficha"><input required value={f.nm.sponsor_id} onChange={setNm('sponsor_id')} className={`${inputCls} w-full`} placeholder="B42" /></Field>
              <Field label="Email"><input type="email" value={f.nm.email} onChange={setNm('email')} className={`${inputCls} w-full`} /></Field>
              <Field label="Teléfono"><input value={f.nm.phone} onChange={setNm('phone')} className={`${inputCls} w-full`} /></Field>
              {f.kind === 'membership' && modeSelect}
              {channelSelect}
            </div>
          ) : (
            <Field label="ID del socio" hint={f.kind === 'reservation' ? 'Para una reserva de alguien que aún no es socio, marca «socio nuevo»' : 'El broker se hereda de su ficha'}>
              <input required value={f.member_id} onChange={set('member_id')} className={`${inputCls} w-48`} placeholder="P12, B40…" />
            </Field>
          )}
          {f.kind === 'reservation' && (
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Precio acordado"><input type="number" required min="0.01" step="0.01" value={f.nm.agreed_price} onChange={setNm('agreed_price')} className={`${inputCls} w-full`} /></Field>
              <Field label="Fecha límite de pago"><input type="date" value={f.nm.due_date} onChange={setNm('due_date')} className={`${inputCls} w-full`} /></Field>
            </div>
          )}
        </>
      )}
      <Field label="Notas"><textarea rows={2} value={f.notes} onChange={set('notes')} className={`${inputCls} w-full resize-none`} /></Field>
      <div className="flex items-center gap-3">
        <button disabled={busy} className={btnGold} data-testid="cdm-registrar-submit">Registrar</button>
        {last && <span className="text-xs text-gray-400">Creado: {Object.entries(last).map(([k, v]) => <Badge key={k} tone="good">{v}</Badge>)}</span>}
      </div>
      <p className="text-xs text-gray-400">Cada registro queda en el historial de cambios. Las altas que se pagan con Stripe en la plataforma se registran solas.</p>
    </form>
  );
}
